import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import { fetch_email_preview } from '../api';
import { email_error_view, fit_preview_html, preview_view } from './adapters';

/*
 * one teacher-month's email preview. The iframe is sandboxed with no scripts and no same-origin,
 * and renders the server's HTML — never re-derived here — with one fit-to-width style in front
 * (adapters.fit_preview_html), so a phone shows the whole email.
 */
const LOAD_FAILED_TEXT = 'Could not load the email preview.';

// The email itself (banners, To/Subject, the sandboxed frame); also the send confirm step's preview.
// show_mode_banner / show_notices false: the send confirm step shows the mode and eligibility itself.
export const PreviewContent = ({
  view,
  teacher_name,
  show_mode_banner = true,
  show_notices = true
}) => (
  <>
    {/*
     * driven by the server's own delivery_mode/recipient_display, never hardcoded — this
     * is what changes first if the test inbox or delivery mode ever does.
     */}
    {show_mode_banner && view.is_test ? (
      <div className="tp-banner tp-banner-warn" role="alert">
        Test mode — every payment email goes to {view.to_display || 'the test inbox'}, never to
        teachers.
      </div>
    ) : null}
    {view.truncated ? (
      <div className="tp-banner tp-banner-info">
        The penalty detail was shortened to fit the email size limit. The full list is in the app.
      </div>
    ) : null}
    {show_notices && view.notice_lines.length ? (
      <div className="tp-banner tp-banner-info tp-em-notice">
        {view.notice_lines.map((line) => (
          <div key={line}>{line}</div>
        ))}
      </div>
    ) : null}
    <dl className="tp-em-meta">
      <dt>To</dt>
      <dd>{view.to_display || 'Not configured'}</dd>
      <dt>Subject</dt>
      <dd>{view.subject}</dd>
    </dl>
    <iframe
      title={`Email preview for ${teacher_name || 'teacher'}`}
      className="tp-em-frame"
      sandbox=""
      srcDoc={fit_preview_html(view.html)}
    />
  </>
);

const PreviewDialog = ({
  pay_month,
  month_label,
  teacher_uuid,
  teacher_name,
  on_close,
  on_focus_lost,
  on_error
}) => {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [view, setView] = useState(null);
  const [error_text, setErrorText] = useState('');
  const seq = useRef(0);
  const on_close_ref = useRef(on_close);
  on_close_ref.current = on_close;
  const on_error_ref = useRef(on_error);
  on_error_ref.current = on_error;

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState('loading');
    fetch_email_preview(pay_month, teacher_uuid)
      .then((data) => {
        if (mine !== seq.current) return;
        setView(preview_view(data));
        setState('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = email_error_view(e, LOAD_FAILED_TEXT);
        if (v.kind === 'permission') {
          on_error_ref.current(e, LOAD_FAILED_TEXT);
          on_close_ref.current();
          return;
        }
        setErrorText(v.text);
        setState('error');
      });
  }, [pay_month, teacher_uuid]);

  useEffect(() => {
    load();
  }, [load]);

  const title = `${teacher_name || 'Teacher'} · ${month_label}`;

  return (
    <TpDialog title={title} on_close={on_close} on_focus_lost={on_focus_lost} size="xl">
      <div className="tp-em-preview tp-em-dialog">
        {state === 'loading' ? <CustomLoading /> : null}
        {state === 'error' ? <LoadFailed text={error_text} on_retry={load} /> : null}
        {state === 'ready' ? <PreviewContent view={view} teacher_name={teacher_name} /> : null}
      </div>
    </TpDialog>
  );
};

export default PreviewDialog;
